/* eslint-disable camelcase -- PostHog's own event property names ($current_url, $set_once, …) */
/**
 * Cleans the URLs PostHog attaches to events before anything leaves the
 * browser. Several v1 URLs carry credentials or contact details: Heyflow's
 * set-password `?email=&phone=`, `/token?token=`, reset password `?hash=`,
 * the `/s/:id` short link. Every URL-valued property keeps only the query
 * keys the onboarding funnel needs (KEPT_QUERY_KEYS), loses its fragment, and
 * has a short-link code replaced with `:code`.
 *
 * Pure and dependency-free so it can be tested without the SDK.
 */

// Property names that hold a URL or a path: $current_url, $referrer,
// $initial_referrer, $pathname, $prev_pageview_pathname, …
const URL_KEY = /(url|referrer|pathname|href|_path)$/i;

// rrweb's Meta event, which records the page URL in `data.href`.
const RRWEB_META = 4;

// `src`: Heyflow vs Perspective landing. `page`: the wizard page number.
const KEPT_QUERY_KEYS = ["src", "page"];

export type SanitizableEvent = {
  event: string;
  properties: Record<string, unknown>;
  $set?: Record<string, unknown>;
  $set_once?: Record<string, unknown>;
};

// "path?query#fragment" → [path, query]; the fragment is always dropped.
function splitQuery(value: string): [string, string] {
  const [noFragment] = value.split("#");
  const cut = noFragment.indexOf("?");
  return cut === -1
    ? [noFragment, ""]
    : [noFragment.slice(0, cut), noFragment.slice(cut + 1)];
}

function keptQuery(query: string): string {
  const params = new URLSearchParams(query);
  const kept = new URLSearchParams();
  for (const key of KEPT_QUERY_KEYS) {
    const value = params.get(key);
    if (value !== null) kept.set(key, value);
  }
  const out = kept.toString();
  return out ? `?${out}` : "";
}

function rewriteShortLink(path: string): string {
  return path.replace(/(^|\/)s\/[^/]+/, "$1s/:code");
}

// The code rewrite runs on the path alone, so it cannot eat the query.
function cleanPath(value: string): string {
  const [path, query] = splitQuery(value);
  return rewriteShortLink(path) + keptQuery(query);
}

export function cleanUrl(value: string): string {
  if (value.startsWith("/")) return cleanPath(value);
  try {
    const url = new URL(value);
    // Non-http schemes (android-app://, mailto:) have an opaque origin.
    if (url.origin === "null") return cleanPath(value);
    return `${url.origin}${cleanPath(url.pathname + url.search)}`;
  } catch {
    // Not an absolute URL: fail closed on any query or fragment, kept keys
    // included. "$direct" and other markers stay as they are.
    return splitQuery(value)[0];
  }
}

function cleanProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  const cleaned: Record<string, unknown> = { ...properties };
  for (const [key, value] of Object.entries(properties)) {
    if (typeof value === "string" && URL_KEY.test(key)) {
      cleaned[key] = cleanUrl(value);
    }
  }
  return cleaned;
}

function cleanSnapshotData(data: unknown): unknown {
  if (!Array.isArray(data)) return data;
  return data.map((item: unknown) => {
    if (typeof item !== "object" || item === null) return item;
    const record = item as { type?: unknown; data?: { href?: unknown } };
    if (record.type !== RRWEB_META || typeof record.data?.href !== "string") {
      return item;
    }
    return {
      ...record,
      data: { ...record.data, href: cleanUrl(record.data.href) },
    };
  });
}

export function sanitizeEvent<T extends SanitizableEvent>(
  event: T | null,
): T | null {
  if (!event) return event;
  const properties = cleanProperties(event.properties);
  if (event.event === "$snapshot" && "$snapshot_data" in properties) {
    properties.$snapshot_data = cleanSnapshotData(properties.$snapshot_data);
  }
  return {
    ...event,
    properties,
    ...(event.$set ? { $set: cleanProperties(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: cleanProperties(event.$set_once) } : {}),
  } as T;
}
